import { useEffect, useRef, useState } from 'react';
import { Loader2, RefreshCw, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { isDesktop } from '../lib/platform';

/**
 * SessionExpiredModal — shown when the heartbeat detects the session was
 * kicked by a sign-in on another device.
 *
 * Tone: friendly, not alarming. No red colors, no warning icons.
 */
export default function SessionExpiredModal({ open, onReclaim, onSignOut }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const signInBtnRef = useRef(null);
  const closeBtnRef = useRef(null);
  const desktop = isDesktop();

  async function handleSignIn() {
    setLoading(true);
    try {
      const returnTo = encodeURIComponent(location.pathname);
      await onReclaim?.();
      navigate(`/login?returnTo=${returnTo}`);
    } catch {
      // onReclaim may do its own error handling
    } finally {
      setLoading(false);
    }
  }

  function handleClose() {
    onSignOut?.();
    navigate(desktop ? '/welcome' : '/');
  }

  // Refs let the keydown effect always call the latest handlers without
  // re-registering the listener on every render. Without this, Enter /
  // Escape fire stale closures that capture old `onReclaim`/`onSignOut`
  // props (App.jsx re-creates them inline on every render).
  const handleSignInRef = useRef(handleSignIn);
  const handleCloseRef = useRef(handleClose);
  handleSignInRef.current = handleSignIn;
  handleCloseRef.current = handleClose;

  // Trap focus within modal when open
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement;
    signInBtnRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleCloseRef.current();
        return;
      }
      if (e.key === 'Enter' && document.activeElement === signInBtnRef.current) {
        handleSignInRef.current();
        return;
      }
      // Basic focus trap between the two buttons
      if (e.key === 'Tab') {
        const focusable = [signInBtnRef.current, closeBtnRef.current].filter(Boolean);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last?.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first?.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      prev?.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-modal-title"
      aria-describedby="session-modal-body"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <h2
            id="session-modal-title"
            className="text-lg font-semibold text-gray-900 mb-2"
          >
            You signed in on another device
          </h2>
          <p id="session-modal-body" className="text-sm text-gray-600 leading-relaxed">
            This device has been signed out because you signed in somewhere else.
            That&rsquo;s fine &mdash; you can sign back in here, or keep using
            Scrivar on the other device.
          </p>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <button
            ref={closeBtnRef}
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <span className="flex items-center gap-2 justify-center">
              <X className="w-4 h-4" aria-hidden="true" />
              Close
            </span>
          </button>
          <button
            ref={signInBtnRef}
            type="button"
            onClick={handleSignIn}
            disabled={loading}
            className="px-4 py-2.5 bg-brand-blue text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            ) : (
              <RefreshCw className="w-4 h-4" aria-hidden="true" />
            )}
            Sign in again
          </button>
        </div>
      </div>
    </div>
  );
}
